import { BaseSyntheticEvent, useEffect, useState } from 'react';

import ls from 'local-storage';
import { Cookies } from 'react-cookie';
import { NavLink, useNavigate } from 'react-router-dom';

import Close from '@mui/icons-material/Close';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';

import { IAuthState } from '../../redux-store/auth/authTypes';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { setIsActiveSession } from '../../redux-store/ipenSession/ipenSessionSlice';
import { RootState } from '../../redux-store/store';
import { calcDaysUntilExpiry } from '../../utils/helpers';
import { Status } from '../../utils/notaryStatus';
import AccountNav from '../AccountNav';
import BasicConfirm from '../Dialogs/BasicConfirm';
import { Logo } from '../fa-logo';
import './_base.scss';

export default function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const account: IAuthState = useAppSelector((state: RootState) => state.auth);
  const config: any = useAppSelector((state: RootState) => state.global.config);
  const ipenSession: any = useAppSelector(
    (state: RootState) => state.ipenSession
  );
  const ipenNotary: any = useAppSelector(
    (state: RootState) => state.ipenNotary
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number | null>(null);
  const [showNavigateAwayConfirm, setShowNavigateAwayConfirm] =
    useState<boolean>(false);
  const cookie = new Cookies();

  const expiringMessage = () => {
    if (daysUntilExpiry && daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
      return 'Notary Commission is Expiring in ' + daysUntilExpiry + ' Days.';
    } else if (daysUntilExpiry != null && daysUntilExpiry <= 0) {
      return 'Your commission has expired.';
    }
  };

  const commissionExpirationDate = ls('CommissionExpirationDate');
  const commissionStatus: any = ls('CommissionStatus');

  const isNotaryPending = () => {
    let isPending = ipenNotary?.notary?.Status === Status.Pending;
    if (commissionStatus != null) {
      isPending = commissionStatus === Status.Pending;
    }
    return isPending;
  };

  const isCommissionExpirationExists = () => {
    const date = cookie.get('commission_expiration');
    return date != null || commissionExpirationDate != null;
  };

  // if a notary is pending and we have leftover cookies set - we need to remove them
  useEffect(() => {
    if (isNotaryPending() && isCommissionExpirationExists()) {
      cookie.remove('commission_expiration', { path: '/' });
    }
  }, []);

  const initials = account?.currentUser?.initials
    ? account?.currentUser?.initials
    : 'NA';
  const open = Boolean(anchorEl);
  const handleClick = (event: BaseSyntheticEvent) => {
    setAnchorEl(event.target);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (commissionExpirationDate != null) {
      setDaysUntilExpiry(calcDaysUntilExpiry(commissionExpirationDate));
    }
  }, [commissionExpirationDate]);

  const isNotificationVisible = () => {
    return (
      !isNotaryPending() && daysUntilExpiry != null && daysUntilExpiry <= 30
    );
  };

  const colorNotification = () =>
    daysUntilExpiry != null && daysUntilExpiry <= 0 ? 'red' : '#E97E1B';

  const handleCloseClick = () => {
    setShowNavigateAwayConfirm(true);
  };

  const handlePromptOnConfirm = () => {
    setShowNavigateAwayConfirm(false);
    dispatch(setIsActiveSession(false));
  };

  const handlePromptOnCancel = () => {
    setShowNavigateAwayConfirm(false);
  };

  const handleUpdateClick = () => {
    navigate('/notary/registration?selectedTab=1&editMode=true');
  };

  return (
    <>
      {ipenSession?.isActiveSession && (
        <>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#4caf50',
              padding: 1,
              textAlign: 'center',
              color: 'white',
              lineHeight: 1,
            }}>
            <Grid item>
              <Typography variant='subtitle1'>
                IPEN SESSION IN PROGRESS
              </Typography>
            </Grid>
            <Grid item>
              <IconButton sx={{ color: 'inherit' }} onClick={handleCloseClick}>
                <Close />
              </IconButton>
            </Grid>
          </Grid>
          <BasicConfirm
            open={showNavigateAwayConfirm}
            title='IPEN Session In Progress'
            showTitle
            content='Are you sure you want to leave your active IPEN Session?'
            primaryAction={handlePromptOnConfirm}
            primaryActionText='Leave Session'
            secondaryAction={handlePromptOnCancel}
            secondaryActionText='Cancel'
          />
        </>
      )}
      <div className='header'>
        <div>
          <NavLink to='/' style={{ textDecoration: 'none' }}>
            <Logo />
          </NavLink>
        </div>
        <div className='expirationText'>
          <div style={{ paddingRight: '20px' }}>
            {isNotificationVisible() && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}>
                  <IconButton
                    sx={{ color: colorNotification }}
                    onClick={handleCloseClick}>
                    <NotificationImportantIcon />
                  </IconButton>
                  <Typography
                    sx={{
                      color: colorNotification,
                      fontWeight: 'bold',
                      fontSize: 16,
                    }}
                    variant='subtitle1'>
                    {expiringMessage()}
                  </Typography>
                  <Button
                    sx={{
                      fontWeight: '600',
                      alignItems: 'center',
                      marginTop: '3px',
                      padding: 0,
                      fontSize: 16,
                      borderBottom: '1px solid #E97E1B',
                      color: colorNotification,
                      borderRadius: 0,
                      marginLeft: '10px',
                    }}
                    variant='text'
                    onClick={handleUpdateClick}>
                    {' '}
                    Update Now!
                  </Button>
                </Box>
              </>
            )}
          </div>
          <div>
            {isNotificationVisible() ? (
              <Badge
                overlap='circular'
                badgeContent={
                  daysUntilExpiry != null && daysUntilExpiry <= 0 ? '!!' : '!'
                }
                color={
                  daysUntilExpiry != null && daysUntilExpiry <= 0
                    ? 'error'
                    : 'warning'
                }>
                <Avatar
                  id='account-nav-button'
                  className='avatar'
                  aria-controls={open ? 'account-nav' : undefined}
                  aria-haspopup='true'
                  aria-expanded={open ? 'true' : undefined}
                  onClick={(e) => handleClick(e)}>
                  {initials as string}
                </Avatar>
              </Badge>
            ) : (
              <Avatar
                id='account-nav-button'
                className='avatar'
                aria-controls={open ? 'account-nav' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={(e) => handleClick(e)}>
                {initials as string}
              </Avatar>
            )}
            <Popover
              id='account-nav'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}>
              <AccountNav
                account={account}
                daysUntilExpiration={daysUntilExpiry}
                onClose={handleClose}
                accountName={account?.currentUser?.name}
                accountEmail={account?.currentUser?.email}
                config={config}
                notary={ipenNotary?.notary ? ipenNotary.notary : null}
                roles={account.currentUser['user_roles']}
              />
            </Popover>
          </div>
        </div>
      </div>
    </>
  );
}
